import React from 'react'
import formStyles from 'styles/form.module.scss'
import { Field } from 'react-final-form'
import Fields from 'components/common/Fields'
import Dropdown from 'react-dropdown'
import DeviceId from 'utils/detectDevice'
import useTracking from 'hooks/useTracking'
import FieldTracking from 'components/FieldTracking'
import {
  required,
  fieldHasErr,
  inputHasErr,
  composeValidators,
} from 'utils/validations'

import { GenderContext } from 'components/fields/registration/GenderContext'

const dayOptions = Array.from({ length: 9 }, (_, i) => '0' + (i + 1))
  .map(num => String(num))
  .concat(Array.from({ length: 22 }, (_, i) => i + 10).map(num => String(num)))

const monthOptions = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const yearOptions = (gender, currentYear) => {
  let legalMarriageAge = gender === 'Male' ? 21 : 18
  let upperYearRange = Number(currentYear) - legalMarriageAge
  let lowerAgeRange = 1950
  let ageRange = upperYearRange - lowerAgeRange + 1
  return Array.from({ length: ageRange }, (_, i) => i + 1950)
    .map(num => String(num))
    .reverse()
}

const genderPronounMap = {
  Male: 'His',
  Female: 'Her',
}

const LabelForDob = () => (
  <GenderContext>
    {({ gender, postedby }) => {
      let pronoun = postedby === 'Self' ? 'Your' : genderPronounMap[gender]
      return (
        <label className={`${formStyles.label} col-md-12`}>
          {pronoun} date of birth
        </label>
      )
    }}
  </GenderContext>
)

const DayField = () => {
  const handleChange = useTracking('day')
  return (
    <>
      <Field name="day" validate={composeValidators(required('day'))}>
        {({ input, meta }) => (
          <div className="col-4">
            {DeviceId.isDesktop() && (
              <Dropdown
                controlClassName="day_selector"
                menuClassName="day_options"
                options={dayOptions}
                value={dayOptions[0]}
                placeholder={'Day'}
                onBlur={handleChange()}
                className={`${inputHasErr(meta) && formStyles.inputError}`}
                {...input}
              />
            )}

            {DeviceId.isMobile() && (
              <select
                data-testid="day_selector"
                className={`form-control ${inputHasErr(meta) &&
                  formStyles.inputError} ${formStyles.selectMobile}`}
                {...input}
              >
                <option value="" hidden>
                  Day
                </option>
                {dayOptions.map(option => {
                  return (
                    <option value={option} key={option}>
                      {option}
                    </option>
                  )
                })}
              </select>
            )}
          </div>
        )}
      </Field>
      <FieldTracking name="day" />
    </>
  )
}

const MonthField = () => {
  const handleChange = useTracking('month')
  return (
    <>
      <Field name="month" validate={composeValidators(required('month'))}>
        {({ input, meta }) => (
          <div className="col-4">
            {DeviceId.isDesktop() && (
              <Dropdown
                controlClassName="month_selector"
                menuClassName="month_options"
                options={monthOptions}
                value={monthOptions[1]}
                placeholder={'Month'}
                onBlur={handleChange()}
                className={`${inputHasErr(meta) && formStyles.inputError}`}
                {...input}
              />
            )}

            {DeviceId.isMobile() && (
              <select
                data-testid="month_selector"
                className={`form-control ${inputHasErr(meta) &&
                  formStyles.inputError} ${formStyles.selectMobile}`}
                {...input}
              >
                <option value="" hidden>
                  Month
                </option>
                {monthOptions.map(option => {
                  return (
                    <option value={option} key={option}>
                      {option}
                    </option>
                  )
                })}
              </select>
            )}
          </div>
        )}
      </Field>
      <FieldTracking name="month" />
    </>
  )
}

const YearField = ({ gender }) => {
  const handleChange = useTracking('year')
  return (
    <>
      <Field name="year" validate={composeValidators(required('year'))}>
        {({ input, meta }) => (
          <div className="col-4">
            {DeviceId.isDesktop() && (
              <Dropdown
                controlClassName="year_selector"
                menuClassName="year_options"
                options={yearOptions(
                  gender.input.value,
                  new Date().getFullYear()
                )}
                value={''}
                onBlur={handleChange()}
                placeholder={'Year'}
                className={`${inputHasErr(meta) && formStyles.inputError}`}
                {...input}
              />
            )}

            {DeviceId.isMobile() && (
              <select
                data-testid="year_selector"
                className={`form-control ${inputHasErr(meta) &&
                  formStyles.inputError} ${formStyles.selectMobile}`}
                {...input}
              >
                <option value="" hidden>
                  Year
                </option>
                {yearOptions(gender.input.value, new Date().getFullYear()).map(
                  option => {
                    return (
                      <option value={option} key={option}>
                        {option}
                      </option>
                    )
                  }
                )}
              </select>
            )}
          </div>
        )}
      </Field>
      <FieldTracking name="year" />
    </>
  )
}

const DoBErrorField = ({ day, month, year }) => {
  const fullDateErr =
    fieldHasErr(day) || fieldHasErr(month) || fieldHasErr(year)
  const fullDateErrMsg = fullDateErr
    ? day.meta.error || month.meta.error || year.meta.error
    : ''

  return fullDateErrMsg ? (
    <p data-testid="dob_error" className={`${formStyles.error} col-md-12`}>
      {fullDateErrMsg}
    </p>
  ) : null
}

const DateOfBirth = () => {
  return (
    <div className="form-row">
      <LabelForDob />
      <Fields names={['gender', 'day', 'month', 'year']}>
        {props => {
          return (
            <>
              <DayField {...props} />
              <MonthField {...props} />
              <YearField {...props} />
              <DoBErrorField {...props} />
            </>
          )
        }}
      </Fields>
    </div>
  )
}

export default DateOfBirth
